import { CUBIC_METER_IN_CUBIC_FEET } from '../constants/VolumeUnits';
import { PaymentUnit } from '../graphql';
import { exhaustiveCheck } from './util';
import type { OrderPorts } from './worldscaleFreight';
import {
  calculateWorldscaleFreight,
  type WorldscaleFreightRateMap,
} from './worldscaleFreight';

/**
 * Calculates billable quantity for FRT (Freight Rate Ton).
 * FRT follows "weight or measure, whichever is greater" (W/M) principle.
 * Standard conversion: 1 MT = 1 CBM for comparison.
 * Returns null only if both mass and volume are null.
 */

export function calculateFrtBillableQuantity(
  mass: number | null,
  volume: number | null,
): number | null {
  if (mass === null && volume === null) return null;
  return Math.max(mass ?? 0, volume ?? 0);
}

export interface FreightCalculationInput {
  mass: number | null;
  volume: number | null;
  pcs: number | null;
  freightRate: number;
  freightUnit: PaymentUnit;
  worldscaleFreightRates?: WorldscaleFreightRateMap;
  order?: OrderPorts;
}

/**
 * Core freight calculation logic shared across income and cost calculations.
 * Handles all payment unit types and returns the calculated freight amount.
 */
export function calculateFreightAmount({
  mass,
  volume,
  pcs,
  freightRate,
  freightUnit,
  worldscaleFreightRates,
  order,
}: FreightCalculationInput): number | null {
  switch (freightUnit) {
    case PaymentUnit.LUMPSUM: {
      return freightRate;
    }
    case PaymentUnit.PER_SET:
    case PaymentUnit.PER_PCS: {
      if (pcs === null) return null;
      return pcs * freightRate;
    }
    case PaymentUnit.FT3: {
      if (volume === null) return null;
      return volume * CUBIC_METER_IN_CUBIC_FEET * freightRate;
    }
    case PaymentUnit.M3:
    case PaymentUnit.SCBM_UB: {
      if (volume === null) return null;
      return volume * freightRate;
    }
    case PaymentUnit.BDMT:
    case PaymentUnit.MT: {
      if (mass === null) return null;
      return mass * freightRate;
    }
    case PaymentUnit.WORLDSCALE: {
      const worldscaleResult = calculateWorldscaleFreight(
        freightRate,
        worldscaleFreightRates,
        order,
        mass,
      );
      return worldscaleResult ? worldscaleResult.freight : null;
    }
    case PaymentUnit.FRT: {
      const billableQuantity = calculateFrtBillableQuantity(mass, volume);
      if (billableQuantity === null) return null;
      return billableQuantity * freightRate;
    }
    default: {
      exhaustiveCheck(freightUnit);
    }
  }
}

/**
 * Inverse of calculateFreightAmount: given a total freight income,
 * derives the per-unit freight rate for the given payment unit.
 * Returns null when the inverse is undefined (zero divisor, WORLDSCALE, etc.).
 */
export function reverseFreightAmount(
  income: number,
  mass: number | null,
  volume: number | null,
  pcs: number | null,
  paymentUnit: PaymentUnit,
): number | null {
  switch (paymentUnit) {
    case PaymentUnit.LUMPSUM:
      return income;

    case PaymentUnit.MT:
    case PaymentUnit.BDMT: {
      if (mass === null || mass === 0) return null;
      return income / mass;
    }

    case PaymentUnit.M3:
    case PaymentUnit.SCBM_UB: {
      if (volume === null || volume === 0) return null;
      return income / volume;
    }

    case PaymentUnit.FT3: {
      if (volume === null || volume === 0) return null;
      return income / (volume * CUBIC_METER_IN_CUBIC_FEET);
    }

    case PaymentUnit.PER_PCS:
    case PaymentUnit.PER_SET: {
      if (pcs === null || pcs === 0) return null;
      return income / pcs;
    }

    case PaymentUnit.FRT: {
      const billableQuantity = calculateFrtBillableQuantity(mass, volume);
      if (billableQuantity === null || billableQuantity === 0) return null;
      return income / billableQuantity;
    }

    case PaymentUnit.WORLDSCALE:
      // WORLDSCALE requires external flat-rate context, not invertible
      return null;
  }
}
